import { createGlobalStyle } from 'styled-components';

/**
 * Global styles for the entire application
 * Sets base styling, typography, and resets for a modern, gaming-friendly design
 * Using a vibrant color palette with clean, readable fonts
 */
const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap');

  :root {
    /* Color variables - vibrant gaming-inspired palette */
    --primary: #3B4CCA;
    --secondary: #FF5350;
    --accent: #FFDE00;
    --text-dark: #2A2A2A;
    --text-light: #666666;
    --text-muted: #8A8A8A;
    --background: #FFFFFF;
    --background-alt: #F8F9FA;
    --border: #E5E5E5;
    --success: #4CAF50;
    --error: #F44336;
    
    /* Typography */
    --font-primary: 'Montserrat', sans-serif;
    --font-secondary: 'Poppins', sans-serif;
    
    /* Spacing */
    --spacing-xs: 0.5rem;
    --spacing-sm: 1rem;
    --spacing-md: 1.5rem;
    --spacing-lg: 3rem;
    --spacing-xl: 5rem;
    --spacing-xxl: 8rem;
    
    /* Breakpoints */
    --breakpoint-sm: 576px;
    --breakpoint-md: 768px;
    --breakpoint-lg: 992px;
    --breakpoint-xl: 1200px;
    
    /* Container widths */
    --container-max-width: 1280px;
    --container-padding: 1.5rem;
  }
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }
  
  body {
    font-family: var(--font-primary);
    color: var(--text-dark);
    line-height: 1.7;
    background-color: var(--background);
    overflow-x: hidden;
    font-weight: 400;
    letter-spacing: 0.01em;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-secondary);
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: var(--spacing-md);
    letter-spacing: -0.01em;
  }
  
  h1 {
    font-size: 2.5rem;
    
    @media (min-width: var(--breakpoint-md)) {
      font-size: 3.5rem;
    }
  }
  
  h2 {
    font-size: 2rem;
    
    @media (min-width: var(--breakpoint-md)) {
      font-size: 2.5rem;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    
    @media (min-width: var(--breakpoint-md)) {
      font-size: 1.75rem;
    }
  }
  
  p {
    margin-bottom: var(--spacing-md);
    font-weight: 300;
  }
  
  a {
    color: var(--primary);
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--secondary);
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  button, .button {
    display: inline-block;
    background-color: var(--primary);
    color: white;
    font-family: var(--font-primary);
    font-weight: 500;
    padding: 0.75rem 2rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    font-size: 0.875rem;
    box-shadow: 0 4px 6px rgba(59, 76, 202, 0.2);
    
    &:hover {
      background-color: var(--secondary);
      transform: translateY(-2px);
      box-shadow: 0 6px 8px rgba(255, 83, 80, 0.25);
    }
    
    &:focus {
      outline: none;
    }
  }
  
  ul, ol {
    margin-bottom: var(--spacing-md);
    padding-left: var(--spacing-lg);
  }
  
  section {
    padding: var(--spacing-xl) 0;
  }
  
  .container {
    width: 100%;
    max-width: var(--container-max-width);
    margin: 0 auto;
    padding: 0 var(--container-padding);
  }
  
  /* Utility classes */
  .text-center {
    text-align: center;
  }
  
  .text-primary {
    color: var(--primary);
  }
  
  .text-secondary {
    color: var(--secondary);
  }
  
  .text-accent {
    color: var(--accent);
  }
  
  .text-muted {
    color: var(--text-muted);
  }
  
  .bg-alt {
    background-color: var(--background-alt);
  }
  
  .mb-0 {
    margin-bottom: 0;
  }
  
  .mb-1 {
    margin-bottom: var(--spacing-xs);
  }
  
  .mb-2 {
    margin-bottom: var(--spacing-sm);
  }
  
  .mb-3 {
    margin-bottom: var(--spacing-md);
  }
  
  .mb-4 {
    margin-bottom: var(--spacing-lg);
  }
  
  .mb-5 {
    margin-bottom: var(--spacing-xl);
  }
  
  .mt-0 {
    margin-top: 0;
  }
  
  .mt-1 {
    margin-top: var(--spacing-xs);
  }
  
  .mt-2 {
    margin-top: var(--spacing-sm);
  }
  
  .mt-3 {
    margin-top: var(--spacing-md);
  }
  
  .mt-4 {
    margin-top: var(--spacing-lg);
  }
  
  .mt-5 {
    margin-top: var(--spacing-xl);
  }
  
  /* Modern divider */
  .divider {
    width: 60px;
    height: 3px;
    background-color: var(--accent);
    margin: 1.5rem auto;
    border-radius: 3px;
  }
  
  /* Smooth scrolling container */
  html.lenis {
    height: auto;
  }
  
  .lenis.lenis-smooth {
    scroll-behavior: auto;
  }
  
  .lenis.lenis-smooth [data-lenis-prevent] {
    overscroll-behavior: contain;
  }
  
  .lenis.lenis-stopped {
    overflow: hidden;
  }
  
  .lenis.lenis-scrolling iframe {
    pointer-events: none;
  }
  
  /* Subtle animations */
  .hover-lift {
    transition: transform 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

export default GlobalStyles;
